import {
  CAMPAIGN_LEVELS,
  RULE_ACTION_LABELS,
  RULE_FREQUENCIES,
  RULE_LEVEL_LABELS,
  RULE_METRICS,
  RULE_METRIC_FORMAT,
  RULE_METRIC_LABELS,
  RULE_OPERATORS,
  RULE_OPERATOR_LABELS,
  RULE_WINDOWS,
  RULE_WINDOW_LABELS,
  roleHas,
  ruleBodySchema,
  type CampaignLevel,
  type CreateRuleInput,
  type RuleAction,
  type RuleCondition,
  type RuleDto,
  type RulePreviewDto,
  type RuleRunDto,
  type RuleTemplateDto,
} from "@iagenix/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Play, Plus, Save, Trash2, Zap } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { FormField } from "@/components/form-field";
import { PageHeader } from "@/components/page-header";
import { PreviewPanel, RunDialog } from "@/components/rules/run-dialog";
import { ErrorState, LoadingState } from "@/components/states";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { errorMessage } from "@/lib/api";
import { api7, queries7, rulesKey } from "@/lib/fase7-api";
import { queries } from "@/lib/queries";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/app/$workspaceId/regras_/$ruleId")({
  component: RegraEditorPage,
});

type Draft = CreateRuleInput;

const NONE = "__nenhum__";

function emptyDraft(): Draft {
  return {
    name: "",
    clientId: null,
    active: true,
    scope: { level: "ads", adAccountMetaIds: [], selection: { mode: "nome" } },
    groups: [{ conditions: [{ metric: "gasto", operator: ">=", value: 50, window: "7d" }] }],
    actions: [{ type: "pausar" }],
    safety: { maxBudget: null, minBudget: null, maxActionsPerItemPerDay: 1, cooldownMinutes: 60 },
    frequencyMinutes: 15,
  };
}

function draftFromRule(rule: RuleDto): Draft {
  return {
    name: rule.name,
    clientId: rule.clientId,
    active: rule.active,
    scope: rule.scope,
    groups: rule.groups,
    actions: rule.actions,
    safety: rule.safety,
    frequencyMinutes: rule.frequencyMinutes,
  };
}

function draftFromTemplate(t: RuleTemplateDto, accounts: string[]): Draft {
  return { ...emptyDraft(), ...t.body, scope: { ...t.body.scope, adAccountMetaIds: accounts } };
}

function RegraEditorPage() {
  const { workspaceId, ruleId } = Route.useParams();
  const isNew = ruleId === "nova";
  const rule = useQuery(queries7.rule(workspaceId, ruleId));
  if (!isNew && rule.isPending) return <LoadingState rows={4} />;
  if (!isNew && rule.isError) return <ErrorState error={rule.error} />;
  const loaded = rule.data;
  if (!isNew && !loaded) return <LoadingState rows={4} />;
  return (
    <RuleEditor
      key={isNew || !loaded ? "nova" : `${ruleId}:${loaded.updatedAt}`}
      workspaceId={workspaceId}
      ruleId={ruleId}
      initial={isNew || !loaded ? emptyDraft() : draftFromRule(loaded)}
    />
  );
}

function RuleEditor({ workspaceId, ruleId, initial }: { workspaceId: string; ruleId: string; initial: Draft }) {
  const isNew = ruleId === "nova";
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const workspace = useQuery(queries.workspace(workspaceId));
  const canWrite = workspace.data ? roleHas(workspace.data.role, "rules:write") : false;
  const templates = useQuery(queries7.ruleTemplates(workspaceId));
  const accounts = useQuery(queries7.ruleAccounts(workspaceId));
  const clients = useQuery(queries.clients(workspaceId));
  const [draft, setDraft] = useState<Draft | null>(initial);
  const [preview, setPreview] = useState<RulePreviewDto | null>(null);
  const [openRun, setOpenRun] = useState<RuleRunDto | null>(null);
  const [confirmRun, setConfirmRun] = useState(false);

  const parsed = useMemo(() => (draft ? ruleBodySchema.safeParse(draft) : null), [draft]);
  const problems = useMemo(() => {
    const map = new Map<string, string>();
    if (parsed && !parsed.success) for (const i of parsed.error.issues) if (!map.has(i.path.join("."))) map.set(i.path.join("."), i.message);
    return map;
  }, [parsed]);
  const [dirty, setDirty] = useState(false);
  const update = (patch: (d: Draft) => Draft) => {
    setDirty(true);
    setDraft((d) => (d ? patch(d) : d));
  };

  // Pré-visualização ao vivo (debounce) sempre que a regra ficar válida.
  const previewMutation = useMutation({
    mutationFn: (body: CreateRuleInput) => api7.previewRule(workspaceId, body),
    onSuccess: (p) => setPreview(p),
    onError: (err) => toast.error(errorMessage(err)),
  });
  const previewKey = parsed?.success ? JSON.stringify({ s: parsed.data.scope, g: parsed.data.groups, a: parsed.data.actions, f: parsed.data.safety }) : "";
  useEffect(() => {
    if (!previewKey || !draft) return;
    const handle = setTimeout(() => previewMutation.mutate(draft), 700);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- só a chave resumida importa
  }, [previewKey]);

  const save = useMutation({
    mutationFn: (body: CreateRuleInput) => (isNew ? api7.createRule(workspaceId, body) : api7.updateRule(workspaceId, ruleId, body)),
    onSuccess: async (saved) => {
      await queryClient.invalidateQueries({ queryKey: rulesKey(workspaceId) });
      setDirty(false);
      toast.success(isNew ? "Regra criada." : "Regra salva.");
      if (isNew) await navigate({ to: "/app/$workspaceId/regras/$ruleId", params: { workspaceId, ruleId: saved.id } });
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const simulate = useMutation({
    mutationFn: () => api7.simulateRule(workspaceId, ruleId),
    onSuccess: async (runs) => {
      await queryClient.invalidateQueries({ queryKey: rulesKey(workspaceId) });
      const first = runs[0];
      if (first) setOpenRun(first);
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const run = useMutation({
    mutationFn: () => api7.runRule(workspaceId, ruleId),
    onSuccess: async (runs) => {
      await queryClient.invalidateQueries({ queryKey: rulesKey(workspaceId) });
      await queryClient.invalidateQueries({ queryKey: ["workspaces", workspaceId, "campaigns"] });
      setConfirmRun(false);
      const first = runs[0];
      if (first) setOpenRun(first);
      toast.success(`Executada: ${runs.reduce((n, r) => n + r.executed, 0)} ação(ões).`);
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  if (!draft) return <LoadingState rows={4} />;
  const d = draft;
  const level = d.scope.level;
  const budgetAction = d.actions.find((a) => a.type.endsWith("_orcamento"));
  const statusAction = d.actions.find((a) => a.type === "pausar" || a.type === "ativar");
  const notifyAction = d.actions.some((a) => a.type === "notificar");
  const setActions = (next: RuleAction[]) => update((x) => ({ ...x, actions: next }));

  return (
    <>
      <PageHeader
        title={isNew ? "Nova regra" : d.name || "Regra"}
        description="Monte SE (condições por métrica, operador, valor e janela; E dentro do grupo, OU entre grupos) → ENTÃO (ações). A prévia mostra quantos itens batem agora."
        actions={
          <>
            <Button asChild variant="outline">
              <Link to="/app/$workspaceId/regras" params={{ workspaceId }}>
                <ArrowLeft /> Voltar
              </Link>
            </Button>
            {!isNew && canWrite ? (
              <>
                <Button variant="outline" onClick={() => simulate.mutate()} loading={simulate.isPending} disabled={dirty}>
                  <Play /> Simular
                </Button>
                <Button variant="outline" onClick={() => setConfirmRun(true)} loading={run.isPending} disabled={dirty}>
                  <Zap /> Executar agora
                </Button>
              </>
            ) : null}
            {canWrite ? (
              <Button onClick={() => (parsed?.success ? save.mutate(d) : toast.error("Confira os campos destacados."))} loading={save.isPending}>
                <Save /> {isNew ? "Criar regra" : "Salvar"}
              </Button>
            ) : null}
          </>
        }
      />
      {!canWrite ? <p className="mb-4 text-sm text-muted-foreground">Você pode ver a regra, mas só Dono, Admin e Gestor editam.</p> : null}

      {isNew && templates.data ? (
        <Card className="mb-4">
          <CardHeader>
            <CardTitle>Começar por um modelo</CardTitle>
            <CardDescription>Escolha um e ajuste os valores. Você também pode montar do zero.</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-2 sm:grid-cols-2">
            {templates.data.map((t) => (
              <button
                key={t.id}
                type="button"
                className="rounded-lg border p-3 text-left transition-colors hover:bg-accent"
                onClick={() => {
                  setDraft(draftFromTemplate(t, d.scope.adAccountMetaIds));
                  setDirty(true);
                }}
              >
                <p className="font-medium">{t.name}</p>
                <p className="text-xs text-muted-foreground">{t.description}</p>
              </button>
            ))}
          </CardContent>
        </Card>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-[1fr_380px]">
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Onde a regra atua</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <FormField id="nome" label="Nome da regra" error={problems.get("name")}>
                  <Input id="nome" value={d.name} onChange={(e) => update((x) => ({ ...x, name: e.target.value }))} disabled={!canWrite} />
                </FormField>
                <FormField id="cliente" label="Cliente (opcional)" hint="Só organiza e define quem recebe o alerta.">
                  <Select value={d.clientId ?? NONE} onValueChange={(v) => update((x) => ({ ...x, clientId: v === NONE ? null : v }))} disabled={!canWrite}>
                    <SelectTrigger id="cliente"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>Nenhum</SelectItem>
                      {clients.data?.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormField>
                <FormField id="nivel" label="Nível">
                  <Select
                    value={level}
                    onValueChange={(v) =>
                      update((x) => ({
                        ...x,
                        scope: { ...x.scope, level: v as CampaignLevel, selection: { mode: "nome" } },
                        actions: v === "ads" ? x.actions.filter((a) => !a.type.endsWith("_orcamento")) : x.actions,
                      }))
                    }
                    disabled={!canWrite}
                  >
                    <SelectTrigger id="nivel"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CAMPAIGN_LEVELS.map((l) => (
                        <SelectItem key={l} value={l}>{RULE_LEVEL_LABELS[l]}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormField>
                <FormField id="freq" label="Frequência">
                  <Select value={String(d.frequencyMinutes ?? 15)} onValueChange={(v) => update((x) => ({ ...x, frequencyMinutes: Number(v) as 15 | 30 | 60 }))} disabled={!canWrite}>
                    <SelectTrigger id="freq"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {RULE_FREQUENCIES.map((f) => (
                        <SelectItem key={f} value={String(f)}>a cada {f} min</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormField>
              </div>
              <div className="space-y-2">
                <Label>Contas de anúncio</Label>
                {problems.get("scope.adAccountMetaIds") ? <p className="text-sm text-destructive">{problems.get("scope.adAccountMetaIds")}</p> : null}
                <div className="grid gap-1 sm:grid-cols-2">
                  {accounts.data?.map((a) => {
                    const checked = d.scope.adAccountMetaIds.includes(a.metaId);
                    return (
                      <label key={a.metaId} className="flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm">
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={!canWrite}
                          onChange={(e) =>
                            update((x) => ({
                              ...x,
                              scope: {
                                ...x.scope,
                                adAccountMetaIds: e.target.checked ? [...x.scope.adAccountMetaIds, a.metaId] : x.scope.adAccountMetaIds.filter((id) => id !== a.metaId),
                              },
                            }))
                          }
                        />
                        <span className="truncate">{a.name}</span>
                        <span className="ml-auto text-xs text-muted-foreground">{a.currency}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
              <SelectionEditor draft={d} update={update} workspaceId={workspaceId} canWrite={canWrite} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>SE…</CardTitle>
              <CardDescription>Dentro de um grupo todas as condições precisam valer (E). Entre grupos, basta um (OU). Valores na moeda da conta; CTR em %.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {d.groups.map((g, gi) => (
                <div key={gi} className="space-y-2 rounded-lg border p-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium">{gi === 0 ? "Grupo 1" : `OU · Grupo ${gi + 1}`}</p>
                    {d.groups.length > 1 && canWrite ? (
                      <Button variant="ghost" size="sm" onClick={() => update((x) => ({ ...x, groups: x.groups.filter((_, i) => i !== gi) }))}>
                        <Trash2 /> Remover grupo
                      </Button>
                    ) : null}
                  </div>
                  {g.conditions.map((c, ci) => (
                    <ConditionRow
                      key={ci}
                      condition={c}
                      label={ci === 0 ? "SE" : "E"}
                      canWrite={canWrite}
                      error={problems.get(`groups.${gi}.conditions.${ci}.value`) ?? problems.get(`groups.${gi}.conditions.${ci}.value2`)}
                      onChange={(next) => update((x) => ({ ...x, groups: x.groups.map((gg, i) => (i === gi ? { conditions: gg.conditions.map((cc, j) => (j === ci ? next : cc)) } : gg)) }))}
                      onRemove={g.conditions.length > 1 ? () => update((x) => ({ ...x, groups: x.groups.map((gg, i) => (i === gi ? { conditions: gg.conditions.filter((_, j) => j !== ci) } : gg)) })) : undefined}
                    />
                  ))}
                  {canWrite ? (
                    <Button variant="outline" size="sm" onClick={() => update((x) => ({ ...x, groups: x.groups.map((gg, i) => (i === gi ? { conditions: [...gg.conditions, { metric: "ctr", operator: "<", value: 1, window: "7d" }] } : gg)) }))}>
                      <Plus /> E condição
                    </Button>
                  ) : null}
                </div>
              ))}
              {canWrite ? (
                <Button variant="outline" size="sm" onClick={() => update((x) => ({ ...x, groups: [...x.groups, { conditions: [{ metric: "frequencia", operator: ">", value: 3, window: "7d" }] }] }))}>
                  <Plus /> OU grupo
                </Button>
              ) : null}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>ENTÃO…</CardTitle>
              {problems.get("actions") ? <p className="text-sm text-destructive">{problems.get("actions")}</p> : null}
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-2 sm:grid-cols-3">
                {(["pausar", "ativar"] as const).map((t) => (
                  <label key={t} className={cn("flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm", statusAction?.type === t && "border-ring bg-accent")}>
                    <input type="radio" name="status" checked={statusAction?.type === t} disabled={!canWrite} onChange={() => setActions([...d.actions.filter((a) => a.type !== "pausar" && a.type !== "ativar"), { type: t }])} />
                    {RULE_ACTION_LABELS[t]}
                  </label>
                ))}
                <label className={cn("flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm", !statusAction && "border-ring bg-accent")}>
                  <input type="radio" name="status" checked={!statusAction} disabled={!canWrite} onChange={() => setActions(d.actions.filter((a) => a.type !== "pausar" && a.type !== "ativar"))} />
                  Não mudar o status
                </label>
              </div>
              {level !== "ads" ? (
                <div className="space-y-2 rounded-lg border p-3">
                  <div className="grid gap-2 sm:grid-cols-[1fr_1fr_1fr]">
                    <Select
                      value={budgetAction?.type ?? NONE}
                      disabled={!canWrite}
                      onValueChange={(v) =>
                        setActions([
                          ...d.actions.filter((a) => !a.type.endsWith("_orcamento")),
                          ...(v === NONE ? [] : [{ type: v as RuleAction["type"], mode: v === "definir_orcamento" ? ("valor" as const) : (budgetAction?.mode ?? "percentual"), value: budgetAction?.value ?? 20 }]),
                        ])
                      }
                    >
                      <SelectTrigger aria-label="Ação de orçamento"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NONE}>Não mexer no orçamento</SelectItem>
                        <SelectItem value="aumentar_orcamento">Aumentar orçamento</SelectItem>
                        <SelectItem value="diminuir_orcamento">Diminuir orçamento</SelectItem>
                        <SelectItem value="definir_orcamento">Definir orçamento</SelectItem>
                      </SelectContent>
                    </Select>
                    {budgetAction ? (
                      <>
                        {budgetAction.type !== "definir_orcamento" ? (
                          <Select value={budgetAction.mode ?? "percentual"} disabled={!canWrite} onValueChange={(v) => setActions(d.actions.map((a) => (a === budgetAction ? { ...a, mode: v as "percentual" | "valor" } : a)))}>
                            <SelectTrigger aria-label="Modo"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="percentual">em %</SelectItem>
                              <SelectItem value="valor">em valor</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <span className="self-center text-sm text-muted-foreground">valor diário/vitalício</span>
                        )}
                        <Input type="number" min={0} step="0.01" aria-label="Valor" value={budgetAction.value ?? ""} disabled={!canWrite} onChange={(e) => setActions(d.actions.map((a) => (a === budgetAction ? { ...a, value: Number(e.target.value) } : a)))} />
                      </>
                    ) : null}
                  </div>
                  <p className="text-xs text-muted-foreground">Cada item mantém o tipo de orçamento que já tem (diário ou vitalício). Campanha ABO e conjunto em CBO são pulados com aviso.</p>
                </div>
              ) : null}
              <label className="flex cursor-pointer items-center gap-2 text-sm">
                <input type="checkbox" checked={notifyAction} disabled={!canWrite} onChange={(e) => setActions(e.target.checked ? [...d.actions, { type: "notificar" }] : d.actions.filter((a) => a.type !== "notificar"))} />
                Notificar (app, e-mail conforme preferências e webhooks do workspace)
              </label>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Limites de segurança</CardTitle>
              <CardDescription>A regra nunca mexe em itens em revisão, reprovados, com problemas ou em processamento.</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <FormField id="max" label="Orçamento máximo (moeda da conta)" error={problems.get("safety.maxBudget")} hint="Vazio = sem teto.">
                <Input id="max" type="number" min={0} step="0.01" value={d.safety?.maxBudget ?? ""} disabled={!canWrite} onChange={(e) => update((x) => ({ ...x, safety: { ...x.safety, maxBudget: e.target.value === "" ? null : Number(e.target.value) } }))} />
              </FormField>
              <FormField id="min" label="Orçamento mínimo (moeda da conta)" hint="Vazio = só o mínimo da Meta.">
                <Input id="min" type="number" min={0} step="0.01" value={d.safety?.minBudget ?? ""} disabled={!canWrite} onChange={(e) => update((x) => ({ ...x, safety: { ...x.safety, minBudget: e.target.value === "" ? null : Number(e.target.value) } }))} />
              </FormField>
              <FormField id="maxdia" label="Máximo de ações por item por dia">
                <Input id="maxdia" type="number" min={1} max={50} value={d.safety?.maxActionsPerItemPerDay ?? 1} disabled={!canWrite} onChange={(e) => update((x) => ({ ...x, safety: { ...x.safety, maxActionsPerItemPerDay: Number(e.target.value) } }))} />
              </FormField>
              <FormField id="cooldown" label="Cooldown entre ações no mesmo item (min)">
                <Input id="cooldown" type="number" min={0} max={1440} value={d.safety?.cooldownMinutes ?? 60} disabled={!canWrite} onChange={(e) => update((x) => ({ ...x, safety: { ...x.safety, cooldownMinutes: Number(e.target.value) } }))} />
              </FormField>
              <label className="flex cursor-pointer items-center gap-2 text-sm sm:col-span-2">
                <input type="checkbox" checked={d.active ?? true} disabled={!canWrite} onChange={(e) => update((x) => ({ ...x, active: e.target.checked }))} />
                Regra ativa (roda sozinha pelo Cron na frequência escolhida)
              </label>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-4">
          <Card className="lg:sticky lg:top-4">
            <CardHeader>
              <CardTitle>Prévia ao vivo</CardTitle>
              <CardDescription>Quantos itens batem agora com os dados sincronizados.</CardDescription>
            </CardHeader>
            <CardContent>
              {parsed && !parsed.success ? (
                <div className="space-y-1 text-sm text-muted-foreground">
                  <p>Complete a regra para ver a prévia:</p>
                  {[...problems.values()].slice(0, 4).map((m, i) => (
                    <Badge key={i} variant="outline" className="mr-1">{m}</Badge>
                  ))}
                </div>
              ) : (
                <PreviewPanel preview={preview} loading={previewMutation.isPending} />
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <ConfirmDialog
        open={confirmRun}
        onOpenChange={setConfirmRun}
        title="Executar a regra agora?"
        description="As ações serão aplicadas na Meta nos itens que baterem, respeitando os limites de segurança."
        confirmLabel="Executar"
        loading={run.isPending}
        onConfirm={() => run.mutate()}
      />
      <RunDialog run={openRun} open={openRun !== null} onOpenChange={(o) => !o && setOpenRun(null)} />
    </>
  );
}

function ConditionRow({
  condition,
  label,
  canWrite,
  error,
  onChange,
  onRemove,
}: {
  condition: RuleCondition;
  label: string;
  canWrite: boolean;
  error: string | undefined;
  onChange: (c: RuleCondition) => void;
  onRemove?: (() => void) | undefined;
}) {
  const fmt = RULE_METRIC_FORMAT[condition.metric];
  const unit = fmt === "money" ? "R$/US$" : fmt === "pct" ? "%" : fmt === "ratio" ? "×" : "";
  return (
    <div className="space-y-1">
      <div className="grid gap-2 sm:grid-cols-[48px_1fr_1fr_120px_120px_1fr_auto]">
        <span className="self-center text-xs font-semibold text-muted-foreground">{label}</span>
        <Select value={condition.metric} disabled={!canWrite} onValueChange={(v) => onChange({ ...condition, metric: v as RuleCondition["metric"] })}>
          <SelectTrigger aria-label="Métrica"><SelectValue /></SelectTrigger>
          <SelectContent>
            {RULE_METRICS.map((m) => (
              <SelectItem key={m} value={m}>{RULE_METRIC_LABELS[m]}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={condition.operator} disabled={!canWrite} onValueChange={(v) => onChange({ ...condition, operator: v as RuleCondition["operator"] })}>
          <SelectTrigger aria-label="Operador"><SelectValue /></SelectTrigger>
          <SelectContent>
            {RULE_OPERATORS.map((o) => (
              <SelectItem key={o} value={o}>{RULE_OPERATOR_LABELS[o]}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input type="number" step="0.01" aria-label={`Valor${unit ? ` (${unit})` : ""}`} placeholder={unit} value={Number.isFinite(condition.value) ? condition.value : ""} disabled={!canWrite} onChange={(e) => onChange({ ...condition, value: Number(e.target.value) })} />
        {condition.operator === "entre" ? (
          <Input type="number" step="0.01" aria-label="e" placeholder="e…" value={condition.value2 ?? ""} disabled={!canWrite} onChange={(e) => onChange({ ...condition, value2: Number(e.target.value) })} />
        ) : (
          <span className="hidden sm:block" />
        )}
        <Select value={condition.window} disabled={!canWrite} onValueChange={(v) => onChange({ ...condition, window: v as RuleCondition["window"] })}>
          <SelectTrigger aria-label="Janela"><SelectValue /></SelectTrigger>
          <SelectContent>
            {RULE_WINDOWS.map((w) => (
              <SelectItem key={w} value={w}>{RULE_WINDOW_LABELS[w]}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {onRemove && canWrite ? (
          <Button variant="ghost" size="icon" aria-label="Remover condição" onClick={onRemove}>
            <Trash2 />
          </Button>
        ) : (
          <span />
        )}
      </div>
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
    </div>
  );
}

function SelectionEditor({ draft, update, workspaceId, canWrite }: { draft: Draft; update: (patch: (d: Draft) => Draft) => void; workspaceId: string; canWrite: boolean }) {
  const sel = draft.scope.selection;
  const [q, setQ] = useState("");
  const firstAccount = draft.scope.adAccountMetaIds[0] ?? "";
  const items = useQuery(queries7.ruleItems(workspaceId, sel.mode === "ids" ? firstAccount : "", draft.scope.level, q));
  const setSel = (next: Draft["scope"]["selection"]) => update((x) => ({ ...x, scope: { ...x.scope, selection: next } }));
  return (
    <div className="space-y-2">
      <Label>Quais itens</Label>
      <div className="grid gap-2 sm:grid-cols-2">
        <label className={cn("flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm", sel.mode === "nome" && "border-ring bg-accent")}>
          <input type="radio" name="sel" checked={sel.mode === "nome"} disabled={!canWrite} onChange={() => setSel({ mode: "nome" })} />
          Filtro por nome (todos, ou que contêm / não contêm um texto)
        </label>
        <label className={cn("flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm", sel.mode === "ids" && "border-ring bg-accent")}>
          <input type="radio" name="sel" checked={sel.mode === "ids"} disabled={!canWrite || draft.scope.adAccountMetaIds.length !== 1} onChange={() => setSel({ mode: "ids", ids: [] })} />
          Seleção explícita (uma conta por vez)
        </label>
      </div>
      {sel.mode === "nome" ? (
        <div className="grid gap-2 sm:grid-cols-2">
          <Input placeholder="Nome contém… (opcional)" value={sel.contains ?? ""} disabled={!canWrite} onChange={(e) => setSel({ mode: "nome", contains: e.target.value || undefined, notContains: sel.notContains })} />
          <Input placeholder="Nome NÃO contém… (opcional)" value={sel.notContains ?? ""} disabled={!canWrite} onChange={(e) => setSel({ mode: "nome", contains: sel.contains, notContains: e.target.value || undefined })} />
        </div>
      ) : (
        <div className="space-y-2">
          <Input placeholder="Buscar pelo nome…" value={q} onChange={(e) => setQ(e.target.value)} />
          {items.isPending ? (
            <p className="text-xs text-muted-foreground">Carregando itens…</p>
          ) : items.isError ? (
            <p className="text-xs text-destructive">{errorMessage(items.error)}</p>
          ) : (
            <div className="max-h-56 space-y-1 overflow-y-auto rounded-md border p-2">
              {items.data.length === 0 ? <p className="text-xs text-muted-foreground">Nenhum item. Abra a conta em Campanhas para sincronizar a estrutura.</p> : null}
              {items.data.map((it) => (
                <label key={it.metaId} className="flex cursor-pointer items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={sel.ids.includes(it.metaId)}
                    disabled={!canWrite}
                    onChange={(e) => setSel({ mode: "ids", ids: e.target.checked ? [...sel.ids, it.metaId] : sel.ids.filter((id) => id !== it.metaId) })}
                  />
                  <span className="truncate">{it.name}</span>
                  <span className="ml-auto text-xs text-muted-foreground">{it.effectiveStatus}</span>
                </label>
              ))}
            </div>
          )}
          <p className="text-xs text-muted-foreground">{sel.ids.length} selecionado(s).</p>
        </div>
      )}
    </div>
  );
}
